import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import produce from 'immer'
import {
  get,
  getDatabase,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  OnDisconnect,
  onDisconnect as onRTDBDisconnect,
  onValue,
  push,
  query,
  ref,
  set,
  update,
} from 'firebase/database'
import { DataSnapshot, QueryConstraint } from '@firebase/database'
import { isEmpty } from 'lodash'
import { Maybe, SimpleMap } from '@vendorflow/types'
import { getApp } from 'firebase/app'
import { getIsConnectedPath, hasApp, useAppName } from 'features/firebase/Firebase.services'
import { isTestEnv } from 'constants/Utils.constants'
import { Logger } from 'features/logging/Logging.services'
import { useProductAnalyticsContext } from 'features/product-analytics/ProductAnalyticsProvider'
import { UseDataResult } from 'features/firebase/Firebase.types'

export interface FirebaseContextValue {
  getFirebaseData<DataType>(path: string, options?: { queryConstraints: QueryConstraint[] }): Promise<DataType | null>
  useFirebaseData<DataType>(
    path: string,
    options?: { queryConstraints: QueryConstraint[] }
  ): UseDataResult<DataType | null>
  useChildAdded(
    path: string,
    callback: (snapshot: DataSnapshot, previousChildName: Maybe<string>) => void,
    options?: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ): void
  useChildChanged(
    path: string,
    callback: (snapshot: DataSnapshot, previousChildName: Maybe<string>) => void,
    options?: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ): void
  useChildRemoved(
    path: string,
    callback: (snapshot: DataSnapshot) => void,
    options?: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ): void
  pushFirebaseData<DataType>(path: string, value: DataType): Promise<void>
  setFirebaseData<DataType>(path: string, value: DataType): Promise<void>
  updateFirebaseData(path: string, values: SimpleMap<any>): Promise<void>
  useFirstEntry<DataType>(result: UseDataResult<SimpleMap<DataType | null> | null>): UseDataResult<DataType | null>
  onDisconnect(path: string, callback: (onDC: OnDisconnect) => Promise<void>): void
}

export const FirebaseContext = createContext<FirebaseContextValue | undefined>(undefined)
export function useFirebaseContext() {
  return useContext(FirebaseContext)!
}

function useDBRef(path: string) {
  const appName = useAppName()
  const appExists = isTestEnv || hasApp(appName)
  const app = useMemo(() => (appExists ? getApp(appName) : null), [appName])
  const db = useMemo(() => (app ? getDatabase(app) : null), [app])

  return useMemo(() => (db ? ref(db, path) : null), [path, db, appName])
}

function useDBQuery(path: string, queryConstraints?: QueryConstraint[]) {
  const dbRef = useDBRef(path)

  return useMemo(() => (dbRef ? query(dbRef, ...(queryConstraints || [])) : null), [path, dbRef, queryConstraints])
}

/**
 *
 * @param path the json path to the RTDB data
 * @param options
 * @param options.queryConstraints the query constraints to apply to the query. This needs to be a memoized array.
 */
export function useFirebaseData<DataType>(
  path: string,
  options?: { queryConstraints: QueryConstraint[] }
): UseDataResult<DataType> {
  const dbQuery = useDBQuery(path, options?.queryConstraints)
  const [result, setResult] = useState<UseDataResult<DataType>>({ data: null, dataUpdatedAt: null })

  // performs a reset if the db query changes
  useEffect(() => {
    setResult({ data: null, dataUpdatedAt: null })
  }, [dbQuery])

  // registers our RTDB value listener
  useEffect(() => {
    if (!dbQuery) return

    return onValue(dbQuery, (snapshot) => {
      setResult(
        produce((prev) => {
          prev.data = snapshot.val()
          prev.dataUpdatedAt = new Date().getTime()
        })
      )
    })
  }, [dbQuery])

  return result
}

/**
 * @description used to perform cleanup on disconnect
 * @param path the path at which to start your update
 * @param callback this needs to be memoized using useCallback. Provides instructions for handling the disconnect
 */
function onDisconnect(path: string, callback: (onDC: OnDisconnect) => Promise<void>): void {
  const isConnectedQuery = useDBQuery(getIsConnectedPath())
  const targetRef = useDBRef(path)
  useEffect(() => {
    if (!isConnectedQuery || !targetRef) return

    return onValue(isConnectedQuery, (snapshot) => {
      if (snapshot.val() === true) {
        void callback(onRTDBDisconnect(targetRef))
      }
    })
  }, [isConnectedQuery, targetRef, callback])
}

function onConnectionChange(callback: (isConnected: boolean) => Promise<void>): void {
  const isConnectedQuery = useDBQuery(getIsConnectedPath())

  useEffect(() => {
    if (!isConnectedQuery) return

    return onValue(isConnectedQuery, (snapshot) => {
      void callback(snapshot.val() as boolean)
    })
  }, [isConnectedQuery, callback])
}

export default function FirebaseProvider({ children }: { children: ReactNode | null }) {
  const { trackEvent } = useProductAnalyticsContext()
  const isConnectInit = useRef(false)
  const appName = useAppName()!
  const handleConnectionChange = useCallback(async (isConnected: boolean) => {
    if (!isConnectInit.current && !isConnected) {
      return
    }

    if (!isConnectInit.current) {
      isConnectInit.current = true
    }

    if (isConnected) {
      Logger.log('Connected to Firebase realtime database.')
    } else {
      Logger.warn('Not connected to Firebase realtime database.')
      trackEvent({ type: 'FirebaseDatabaseNotConnected' })
    }
  }, [])
  onConnectionChange(handleConnectionChange)

  async function getFirebaseData<DataType>(
    path: string,
    options?: { queryConstraints: QueryConstraint[] }
  ): Promise<DataType | null> {
    const app = getApp(appName)
    const db = getDatabase(app)
    const snapshot = await get(query(ref(db, path), ...(options?.queryConstraints || [])))
    return snapshot.exists() ? (snapshot.val() as DataType) : null
  }

  function useChildAdded(
    path: string,
    callback: (snapshot: DataSnapshot, previousChildName: Maybe<string>) => void,
    options: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ) {
    const enabled = options?.enabled ?? true
    const dbQuery = useDBQuery(path, options?.queryConstraints)
    useEffect(() => {
      if (!enabled || !dbQuery) return
      return onChildAdded(dbQuery, callback)
    }, [path, dbQuery, enabled])
  }

  function useChildChanged(
    path: string,
    callback: (snapshot: DataSnapshot, previousChildName: Maybe<string>) => unknown,
    options: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ): void {
    const enabled = options?.enabled ?? true
    const dbQuery = useDBQuery(path, options?.queryConstraints)
    useEffect(() => {
      if (!enabled || !dbQuery) return

      return onChildChanged(dbQuery, callback)
    }, [path, dbQuery, enabled])
  }

  function useChildRemoved(
    path: string,
    callback: (snapshot: DataSnapshot) => unknown,
    options: { enabled?: boolean; queryConstraints?: QueryConstraint[] }
  ) {
    const enabled = options?.enabled ?? true
    const dbQuery = useDBQuery(path, options?.queryConstraints)
    useEffect(() => {
      if (!enabled || !dbQuery) return

      return onChildRemoved(dbQuery, callback)
    }, [path, dbQuery, enabled])
  }

  /**
   * @description used to insert a new node in the realtime database with an auto-generated id as its key
   * @param path the path to the node you would like to push to
   * @param value the value you will push at the path
   */
  async function pushFirebaseData<DataType>(path: string, value: DataType): Promise<void> {
    const db = getDatabase(getApp(appName))
    await push(ref(db, path), value)
  }

  /**
   * @description used to update a very specific node in the realtime database
   * @param path the path to the node you would like to update
   * @param value the value you will set at the path
   */
  async function setFirebaseData<DataType>(path: string, value: DataType): Promise<void> {
    const db = getDatabase(getApp(appName))
    await set(ref(db, path), value)
  }

  /**
   * @description used to update a set of nodes in the realtime database
   * @param path the path at which to start your update
   * @param values an object where the properties are the relative paths (or just a property at the starting path) AND the values
   * are the intended updates at each of those paths
   */
  async function updateFirebaseData(path: string, values: SimpleMap<any>): Promise<void> {
    const db = getDatabase(getApp(appName))
    await update(ref(db, path), values)
  }

  return (
    <FirebaseContext.Provider
      value={{
        getFirebaseData,
        useFirebaseData,
        useChildAdded,
        useChildChanged,
        useChildRemoved,
        pushFirebaseData,
        setFirebaseData,
        updateFirebaseData,
        useFirstEntry,
        onDisconnect,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}

export function useFirstEntry<DataType>(
  result: UseDataResult<SimpleMap<DataType | null>>
): UseDataResult<DataType | null> {
  return useMemo(() => {
    const value = !isEmpty(result.data) ? (Object.entries(result.data!)[0][1] as DataType) : null
    return { data: value, dataUpdatedAt: result.dataUpdatedAt }
  }, [result.data, result.dataUpdatedAt])
}
